import { Injectable, inject } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
    BehaviorSubject,
    filter,
    map,
    Observable,
    shareReplay,
    tap,
} from 'rxjs';
import { PortalEsploroSendToActionsConfiguration } from '../../shared/interfaces/research-portal/portal-configuration/portal-esploro-send-to-actions-configuration.interface';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { ExportersService, ExportTarget } from '@exl-ng/mulo-common';
import { AssetPageService } from '../asset-page.service';
import { WindowRefService } from '../../core/window-ref.service';
import { FullAssetPage } from '../../shared/configurations/full-asset-page.constant';
import { HttpClient, HttpParams } from '@angular/common/http';
import { RestApiBase } from '../../shared/configurations/rest-api-base.config';
import { UrlUtils } from '../../shared/utils/url.utils';
import { FullAssetPageIdentifiersConfiguration } from '../../shared/interfaces/research-portal/portal-configuration/full-asset-page-identifiers-configuration.interface';

@Injectable({
    providedIn: 'root',
})
export class AssetExportService {
    private xl8Svc = inject(TranslateService);
    private exportersService = inject(ExportersService);
    private assetPageService = inject(AssetPageService);
    private windowRef = inject(WindowRefService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private http = inject(HttpClient);

    readonly CITATION_PREFIX_LABEL = 'citation.labels.';
    citationListFromMT: string[];

    private assetExportTargetsSubject = new BehaviorSubject([]);
    exportTargets$: Observable<ExportTarget[]>;
    identifiersConfig: FullAssetPageIdentifiersConfiguration[];

    private exportOutputSubject = new BehaviorSubject('');
    exportOutput$ = this.exportOutputSubject.asObservable();

    institutionCode = UrlUtils.getParam('institution');

    private parser = new DOMParser();

    constructor() {
        const configSvc = inject(ConfigurationHandlerService);

        const defaultAssetConfig$ = configSvc.config$.pipe(
            map(
                (config) =>
                    config.portalViewConfiguration?.configurationByView?.default
                        ?.configurationByAssetType?.default,
            ),
        );
        this.exportTargets$ = defaultAssetConfig$.pipe(
            map((config) => config.sendToActions || []),
            map((actions) => this.setExportTargetsByConfig(actions)),
            tap((targets) => this.setAssetExportTargets(targets)),
            map((targets) => targets.filter((item) => item.active)),
            shareReplay(),
        );

        defaultAssetConfig$
            .pipe(
                map((config) => {
                    const idConfig = config?.essentials?.find(
                        (essential) => essential.code === 'identifiers',
                    );
                    if (idConfig?.active) {
                        return idConfig.identifiers.filter((_) => _.active);
                    } else {
                        return undefined;
                    }
                }),
                filter((identifiers) => identifiers !== undefined),
                tap((identifiers) => {
                    this.identifiersConfig = identifiers;
                }),
            )
            .subscribe();
        this.getCitationListFromMt();
    }

    get assetExportTargets() {
        return this.assetExportTargetsSubject.getValue();
    }

    get exportOutput() {
        return this.exportOutputSubject.getValue();
    }

    setAssetExportTargets(targets) {
        this.assetExportTargetsSubject.next([...targets]);
    }

    initExportTargets(): ExportTarget[] {
        const emailControl = new UntypedFormControl('', [
            Validators.email,
            Validators.required,
        ]);
        const disabledSub = new BehaviorSubject(emailControl.invalid);
        emailControl.valueChanges.subscribe(() =>
            disabledSub.next(emailControl.invalid),
        );
        const xl8 = (code: string | string[], params?: Object) =>
            this.xl8Svc.instant(code, params);
        return [
            {
                value: 'email',
                name: xl8(
                    'esploro.research.conf.research.portal.label.prefix.eMail',
                ),
                actions: [
                    {
                        label: xl8(
                            'research.portal.asset.sendTo.export.action.email',
                        ),
                        value: 'email',
                        successMsg: xl8(
                            'research.portal.asset.sendTo.export.email.success',
                        ),
                        disabled$: disabledSub,
                    },
                ],
                targetOptions: [
                    {
                        type: 'email',
                        label: xl8(
                            'research.portal.asset.sendTo.export.email.input.field',
                        ),
                        control: emailControl,
                        errorMessage: xl8(
                            'research.portal.asset.sendTo.export.email.error',
                        ),
                        autocomplete: 'email',
                    },
                ],
                icon: 'email-outline',
            },
            {
                value: 'bibtex',
                name: xl8('research.portal.asset.sendTo.export.targets.bibtex'),
                actions: [
                    {
                        label: xl8(
                            'research.portal.fulldisplay.sendTo.export.dialog.download',
                            { value: '.bib' },
                        ),
                        value: 'download',
                    },
                ],
                preview: 'code',
                onTargetChange: (items) => this.bibTexExport(items),
            },
            {
                value: 'ris',
                name: xl8('research.portal.asset.sendTo.export.targets.ris'),
                actions: [
                    {
                        label: xl8(
                            'research.portal.fulldisplay.sendTo.export.dialog.download',
                            { value: '.ris' },
                        ),
                        value: 'download',
                    },
                ],
                preview: 'code',
                onTargetChange: (items) => this.risExport(items),
            },
            {
                value: 'citation',
                name: xl8(
                    'research.portal.asset.sendTo.export.targets.citation',
                ),
                icon: 'quote-open',
                preview: 'rich',
                actions: [
                    {
                        label: xl8(
                            'research.portal.asset.sendTo.export.action.print',
                        ),
                        value: 'print',
                    },
                    {
                        label: xl8(
                            'research.portal.fulldisplay.sendTo.export.dialog.download',
                            { value: '.txt' },
                        ),
                        value: 'download',
                    },
                ],
                targetOptions: [
                    {
                        type: 'select',
                        label: xl8(
                            'research.portal.fulldisplay.sendTo.export.action.citation.style',
                        ),
                        value: 'style',
                        selected: 'apa',
                    },
                ],
                afterPreviewNote: xl8(
                    'research.portal.fulldisplay.sendTo.export.action.citation.note',
                ),
                onTargetChange: (items, options) =>
                    this.getCitation(items, options?.[0]?.selected),
            },
            {
                value: 'print',
                name: xl8('research.portal.asset.sendTo.export.targets.print'),
                icon: 'printer',
                actions: [
                    {
                        label: xl8(
                            'research.portal.asset.sendTo.export.action.print',
                        ),
                        value: 'print',
                    },
                ],
            },
            {
                value: 'refworks',
                name: xl8(
                    'research.portal.asset.sendTo.export.targets.refworks',
                ),
                actions: [
                    {
                        label: xl8(
                            'research.portal.asset.sendTo.export.action.refworks',
                        ),
                        value: 'send',
                    },
                ],
                onTargetChange: (items) => this.risExport(items, false),
            },
            {
                value: 'endnote',
                name: xl8(
                    'research.portal.asset.sendTo.export.targets.endnote',
                ),
                actions: [
                    {
                        label: xl8(
                            'research.portal.asset.sendTo.export.action.endnote',
                        ),
                        value: 'send',
                    },
                ],
            },
        ];
    }

    setExportTargetsByConfig(
        actions: PortalEsploroSendToActionsConfiguration[],
    ) {
        const exportTargets = this.initExportTargets();
        const _targets = [];

        actions.forEach((action) => {
            const target = exportTargets.find(
                (item) =>
                    item.value.toLowerCase() === action.code.toLowerCase() ||
                    item.value.toLowerCase() ===
                        action.code.toLowerCase() + 's',
            );
            if (
                action.code.toLowerCase() === 'citation' &&
                this.citationListFromMT.length &&
                target
            ) {
                target['targetOptions'] = [
                    {
                        type: 'select',
                        label: this.xl8Svc.instant(
                            'research.portal.fulldisplay.sendTo.export.action.citation.style',
                        ),
                        value: 'style',
                        selected: this.getDefaultCitationStyle(
                            this.citationListFromMT,
                        ),
                        ...{
                            items: [
                                ...this.citationListFromMT.map((obj) => ({
                                    label: this.xl8Svc.instant(
                                        this.CITATION_PREFIX_LABEL + obj,
                                    ),
                                    value: obj,
                                })),
                            ],
                        },
                    },
                ];
            }
            if (target) {
                target['active'] = action.active;
                _targets.push(target);
            }
        });
        return _targets;
    }

    getDefaultCitationStyle(styles: string[]) {
        if (styles.indexOf('apa') !== -1) {
            return 'apa';
        }
        return styles[0];
    }

    getMmsIdsFromItems(items: any[]) {
        let mmsIds: string[];
        if (typeof items[0] !== 'string') {
            mmsIds = items
                .filter((item) => !!item.id)
                .map((item) => item.id.toString());
        } else {
            mmsIds = items;
        }
        return mmsIds;
    }

    getCitation(items: any[], style: string) {
        const mmsIDs = this.getMmsIdsFromItems(items);

        this.assetPageService.getCitation(mmsIDs, style).subscribe((data) => {
            this.exportOutputSubject.next(data.join('<br>'));
            this.exportersService.updatePreview(data.join('<br>'));
        });
    }

    risExport(items: any[], updatePreview = true) {
        const mmsIds = this.getMmsIdsFromItems(items);

        let params = new HttpParams();
        params = params.set('institution', this.institutionCode);
        return this.http
            .post(
                RestApiBase.PUSH_TO_RIS,
                { mmsIds },
                {
                    params: params,
                    responseType: 'text',
                },
            )
            .subscribe((data) => {
                this.exportOutputSubject.next(data);
                if (updatePreview) {
                    this.exportersService.updatePreview(data);
                }
            });
    }

    bibTexExport(items: any[]) {
        const mmsIds = this.getMmsIdsFromItems(items);

        let params = new HttpParams();
        params = params.set('institution', this.institutionCode);
        this.http
            .post(
                RestApiBase.PUSH_TO_BIBTEX,
                { mmsIds },
                {
                    params: params,
                    responseType: 'text',
                },
            )
            .subscribe((data) => {
                this.exportOutputSubject.next(data);
                this.exportersService.updatePreview(data);
            });
    }

    handleExportActionClick(change, mmsId) {
        switch (change.action.value) {
            case 'download':
                this.handleDownloadAction(change);
                break;
            case 'print':
                this.handlePrintAction(change);
                break;
            case 'send':
                this.handleSendAction(change, mmsId);
                break;
            case 'email':
                this.handleEmailAction(change, mmsId);
                break;
        }
    }

    handleDownloadAction(change) {
        if (change.target.value === 'ris') {
            this.downloadFile(change.target.name, '.ris');
        } else if (change.target.value === 'bibtex') {
            this.downloadFile(change.target.name, '.bib');
        } else {
            this.downloadFile(change.target.name, '.txt');
        }
    }

    handleSendAction(change, mmsId) {
        if (change.target.value === 'refworks') {
            const mapForm = document.createElement('form');
            mapForm.target = 'refworks-window';
            mapForm.method = 'POST';
            mapForm.acceptCharset = 'UTF-8';
            mapForm.action = FullAssetPage.REFWORKS_URL;

            const mapInput = document.createElement('input');
            mapInput.type = 'hidden';
            mapInput.name = 'ImportData';
            mapInput.setAttribute('value', this.exportOutput);
            mapForm.appendChild(mapInput);

            document.body.appendChild(mapForm);
            this.windowRef.nativeWindow.open('', 'refworks-window');
            mapForm.submit();
            document.body.removeChild(mapForm);
        } else if (change.target.value === 'endnote') {
            let serviceUrl =
                location.origin +
                FullAssetPage.GET_EXPORT_RIS +
                '/' +
                mmsId +
                '?institution=' +
                this.institutionCode;
            serviceUrl = encodeURIComponent(serviceUrl);
            this.windowRef.nativeWindow.open(
                FullAssetPage.ENDNOTE_URL + serviceUrl,
                '_blank',
            );
        }
    }

    handleEmailAction(change, mmsID) {
        this.assetPageService
            .sendAssetByMail(mmsID, change.target.targetOptions[0].value)
            .subscribe(() =>
                this.exportersService.onActionSuccess(change.action),
            );
    }

    handlePrintAction(change) {
        if (change.target.value === 'citation') {
            const htmlString = this.exportOutput;

            const printWindow = window.open('', '_blank');
            printWindow.document.open();
            printWindow.document.write(htmlString);
            printWindow.document.close();
            printWindow.print();
            printWindow.close();
        } else {
            setTimeout(() => {
                this.windowRef.nativeWindow.print();
            });
        }
    }

    downloadFile(source, fileExt) {
        const textContent = this.parser.parseFromString(
            this.exportOutput,
            'text/html',
        ).body.textContent;

        const blob = new Blob([textContent], {
            endings: 'native',
            type: 'text/plain',
        });
        const filename = source + '_Export' + fileExt;
        const url = this.windowRef.nativeWindow.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.download = filename;
        a.href = url;
        a.click();
    }

    getEmailFromAddress() {
        if (
            this.configurationHandlerService.portalConfigurationByDefaultAssetType()
                .sendToActions
        ) {
            const emailObj = this.configurationHandlerService
                .portalConfigurationByDefaultAssetType()
                .sendToActions.filter((action) => action.code === 'eMail');
            if (
                emailObj &&
                emailObj[0] &&
                emailObj[0]['email'] &&
                emailObj[0]['email']['fromAddress']
            ) {
                return emailObj[0]['email']['fromAddress'];
            }
        }
        return '';
    }

    getEmailSubject() {
        if (
            this.configurationHandlerService.portalConfigurationByDefaultAssetType()
                .sendToActions
        ) {
            const emailObj = this.configurationHandlerService
                .portalConfigurationByDefaultAssetType()
                .sendToActions.filter((action) => action.code === 'eMail');
            if (
                emailObj &&
                emailObj[0] &&
                emailObj[0]['email'] &&
                emailObj[0]['email']['subject']
            ) {
                return emailObj[0]['email']['subject'];
            }
        }
        return '';
    }

    clearOutput() {
        this.exportOutputSubject.next('');
    }

    getCitationListFromMt() {
        this.citationListFromMT = [];
        this.assetPageService.getCitationList().subscribe((data) => {
            this.citationListFromMT = data;
        });
    }
}
